import React, { useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { SubPageLayout } from '@/components/layout/SubPageLayout';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { useTrainingVideo } from '@/features/roll-review/hooks/useTrainingVideos';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/contexts/I18nContext';
import { SOSSIETY_RED } from '@/styles/brand';

const CANCEL_REASONS = [
  'cancel_request.reason_another_instructor',
  'cancel_request.reason_wrong_video',
  'cancel_request.reason_changed_mind',
  'cancel_request.reason_other',
] as const;

const parseBeltLabel = (beltRank?: string, t?: any): string => {
  const rank = beltRank?.toLowerCase() ?? '';
  if (!t) return 'Black Belt';
  if (rank.includes('black'))  return t('roll_review.black_belt');
  if (rank.includes('brown'))  return t('roll_review.brown_belt');
  if (rank.includes('purple')) return t('roll_review.purple_belt');
  if (rank.includes('blue'))   return t('roll_review.blue_belt');
  return t('roll_review.white_belt');
};

const getInitials = (fullName?: string): string => {
  if (!fullName) return '?';
  return fullName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
};

const CancelRequest: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();

  const { data: video, isLoading } = useTrainingVideo(videoId);

  const [selectedReason, setSelectedReason] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const handleCancel = async () => {
    if (!video) return;
    setIsCancelling(true);
    setCancelError(null);

    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/roll-review-cancel`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            training_video_id: video.id,
            instructor_id:     video.reviewer_id, // fallback when training_video_id not linked
          }),
        }
      );

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to cancel review');
      }

      navigate('/my-reviews');
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Unexpected error');
    } finally {
      setIsCancelling(false);
    }
  };

  if (isLoading) {
    return (
      <SubPageLayout title={t('cancel_request.title')}>
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-white/20 border-t-white/70 rounded-full animate-spin" />
        </div>
      </SubPageLayout>
    );
  }

  if (!video) {
    return (
      <SubPageLayout title={t('cancel_request.title')}>
        <div className="flex items-center justify-center py-20">
          <p className="text-white/40 text-sm">{t('cancel_request.not_found')}</p>
        </div>
      </SubPageLayout>
    );
  }

  // Only the practitioner who submitted the video may cancel it.
  if (video.uploader_id !== user?.id) {
    return <Navigate to="/my-reviews" replace />;
  }

  const instructor = video.reviewer;
  const instructorName = instructor?.full_name ?? t('my_reviews.unknown_instructor');
  const beltLabel = parseBeltLabel(instructor?.belt_rank, t);
  const submittedDate = format(new Date(video.created_at), 'MMM d');
  const excerpt = video.description
    ? (video.description.length > 55 ? video.description.slice(0, 55) + '…' : video.description)
    : '';

  const selectedReasonLabel = selectedReason ? t(selectedReason as any) : t('cancel_request.choose_reason');

  return (
    <SubPageLayout title={t('cancel_request.title')}>
      <div className="flex flex-col items-center pt-6 pb-8 gap-6">

        {/* Icon */}
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${SOSSIETY_RED}22` }}
        >
          <XCircle size={32} style={{ color: SOSSIETY_RED }} />
        </div>

        {/* Heading */}
        <div className="text-center px-4">
          <h2 className="text-white text-xl font-bold">{t('cancel_request.heading')}</h2>
          <p className="text-white/50 text-sm mt-2 leading-relaxed">
            {t('cancel_request.description', { instructor: instructorName })}
          </p>
        </div>

        {/* Instructor card */}
        <div
          className="w-full rounded-2xl border border-white/8 p-4 flex flex-col gap-3"
          style={{ backgroundColor: '#1E1010' }}
        >
          <div className="flex items-center gap-3">
            <FramedAvatar
              src={instructor?.profile_picture_url}
              alt={instructorName}
              initials={getInitials(instructorName)}
              size={36}
              frameSize={{ w: 48, h: 52 }}
              beltRank={instructor?.belt_rank ?? 'black'}
              isManual
            />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-bold leading-tight truncate">{instructorName}</p>
              <p className="text-white/40 text-xs mt-0.5">
                {beltLabel} · {t('cancel_request.submitted_on', { date: submittedDate })}
              </p>
            </div>
          </div>

          {excerpt ? (
            <p className="text-white/60 text-sm leading-snug">"{excerpt}"</p>
          ) : null}

          {(video as any).duration_label && (
            <p className="text-white/30 text-xs">{(video as any).duration_label}</p>
          )}
        </div>

        {/* Reason dropdown */}
        <div className="w-full">
          <p className="text-white/40 text-xs font-semibold tracking-widest uppercase mb-2">
            {t('cancel_request.reason_label')}
          </p>
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(o => !o)}
              className="w-full text-left px-4 py-3.5 rounded-xl text-sm transition-colors border border-white/10"
              style={{ backgroundColor: '#1E1010', color: selectedReason ? 'white' : 'rgba(255,255,255,0.35)' }}
            >
              {selectedReasonLabel}
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30">
                {dropdownOpen ? '▲' : '▼'}
              </span>
            </button>

            {dropdownOpen && (
              <div
                className="absolute z-10 mt-1 w-full rounded-xl border border-white/10 overflow-hidden"
                style={{ backgroundColor: '#2A1515' }}
              >
                {CANCEL_REASONS.map((key) => (
                  <button
                    key={key}
                    onClick={() => { setSelectedReason(key); setDropdownOpen(false); }}
                    className="w-full text-left px-4 py-3 text-sm text-white/80 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                  >
                    {t(key as any)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="w-full flex flex-col gap-3 pt-2">
          {cancelError && (
            <div className="rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3">
              <p className="text-sm text-red-300">{cancelError}</p>
            </div>
          )}

          <button
            onClick={handleCancel}
            disabled={isCancelling}
            className="w-full py-4 rounded-xl text-white font-semibold text-sm transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ backgroundColor: SOSSIETY_RED }}
          >
            {isCancelling ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : null}
            {t('cancel_request.confirm_button')}
          </button>

          <button
            onClick={() => navigate(-1)}
            disabled={isCancelling}
            className="w-full py-4 rounded-xl text-white/70 font-semibold text-sm border border-white/15 hover:border-white/30 transition-colors"
          >
            {t('cancel_request.keep_button')}
          </button>
        </div>

      </div>
    </SubPageLayout>
  );
};

export default CancelRequest;
